import React, { useState } from "react";
import axios from "axios";
import { Button, Phone, SearchLg, Spinner, TextInput, TileGroup } from "@opendate-app/setlist";

import { toastSuccess, toastError } from "../../../../shared/toastHelper";

const formatPhoneNumber = (phone) => {
  if (!phone) return "No phone number";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

const styles = {
  current: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  phoneIcon: {
    width: 20,
    height: 20,
    color: "var(--sl-fg-quaternary)",
  },
  number: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: 16,
    borderRadius: 8,
    border: "1px solid var(--sl-border-secondary)",
    background: "var(--sl-bg-primary)",
  },
  heading: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  muted: {
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
  search: {
    display: "flex",
    alignItems: "flex-end",
    gap: 12,
  },
  areaCode: {
    width: 120,
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: "12px 0",
    textAlign: "center",
  },
  results: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
  },
};

const PhoneNumberManager = ({
  teamId,
  venueOwnershipId,
  initialPhoneNumber,
  csrfToken,
}) => {
  const [showPhoneChange, setShowPhoneChange] = useState(false);
  const [areaCode, setAreaCode] = useState("");
  const [availablePhones, setAvailablePhones] = useState([]);
  const [searchingPhones, setSearchingPhones] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [changingPhone, setChangingPhone] = useState(false);
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState(initialPhoneNumber);

  const searchPhones = async () => {
    if (!areaCode || areaCode.length !== 3) {
      toastError("Please enter a valid 3-digit area code");
      return;
    }

    setSearchingPhones(true);
    setAvailablePhones([]);
    setSelectedPhone(null);
    setHasSearched(true);
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const response = await axios.get(
        `/teams/${teamId}/venue_ownerships/${venueOwnershipId}/twilio_account/available_phones`,
        { params: { area_code: areaCode } }
      );
      setAvailablePhones(response.data.phone_numbers || []);
    } catch (error) {
      toastError(error.response?.data?.error || "Failed to search phone numbers");
    } finally {
      setSearchingPhones(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && areaCode.length === 3) {
      searchPhones();
    }
  };

  const handleChangePhone = async () => {
    if (!selectedPhone) return;

    setChangingPhone(true);
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const response = await axios.post(
        `/teams/${teamId}/venue_ownerships/${venueOwnershipId}/twilio_account/change_phone`,
        { phone_number: selectedPhone }
      );
      toastSuccess("Phone number changed successfully");
      setCurrentPhoneNumber(response.data.phone_number);
      cancelPhoneChange();
    } catch (error) {
      toastError(error.response?.data?.error || "Failed to change phone number");
    } finally {
      setChangingPhone(false);
    }
  };

  const cancelPhoneChange = () => {
    setShowPhoneChange(false);
    setAreaCode("");
    setAvailablePhones([]);
    setSelectedPhone(null);
    setHasSearched(false);
  };

  if (!showPhoneChange) {
    return (
      <div style={styles.current}>
        <Phone style={styles.phoneIcon} aria-hidden="true" />
        <div style={styles.number}>
          {formatPhoneNumber(currentPhoneNumber)}
        </div>
        {venueOwnershipId && (
          <Button variant="link-color" onClick={() => setShowPhoneChange(true)}>
            Change
          </Button>
        )}
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div>
        <div style={styles.heading}>Change Phone Number</div>
        <div style={styles.muted}>
          Search by area code to find available numbers in your region.
        </div>
      </div>

      <div style={styles.search}>
        <div style={styles.areaCode}>
          <TextInput
            label="Area Code"
            type="text"
            inputMode="numeric"
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
            onKeyDown={handleKeyDown}
            placeholder="e.g. 415"
            maxLength={3}
          />
        </div>
        <Button
          icon={SearchLg}
          onClick={searchPhones}
          disabled={searchingPhones || areaCode.length !== 3}
        >
          {searchingPhones ? "Searching" : "Search"}
        </Button>
      </div>

      {searchingPhones && (
        <div style={styles.centered}>
          <Spinner aria-label="Searching phone numbers" />
        </div>
      )}

      {!searchingPhones && hasSearched && availablePhones.length === 0 && (
        <div style={{ ...styles.centered, ...styles.muted }}>
          No numbers available in area code {areaCode}. Try another area code.
        </div>
      )}

      {!searchingPhones && availablePhones.length > 0 && (
        <div style={styles.results}>
          <div style={styles.muted}>
            {availablePhones.length} number{availablePhones.length !== 1 ? "s" : ""} available
          </div>
          <TileGroup
            type="radio"
            name="available-phone-numbers"
            aria-label="Available phone numbers"
            columns={2}
            value={selectedPhone}
            onChange={setSelectedPhone}
            items={availablePhones.map((phone) => ({
              value: phone.phone_number,
              label: formatPhoneNumber(phone.phone_number),
              supportingText: [phone.locality, phone.region].filter(Boolean).join(", ") || undefined,
            }))}
          />
        </div>
      )}

      <div style={styles.footer}>
        <Button onClick={cancelPhoneChange} disabled={changingPhone}>
          Cancel
        </Button>
        {selectedPhone && (
          <Button variant="primary" onClick={handleChangePhone} disabled={changingPhone}>
            {changingPhone ? "Changing" : "Confirm Change"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PhoneNumberManager;
